import {
  SourceEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  FeeSchedule,
  serviceCodeToFee,
  ServiceProviderHandler,
} from '@principle-theorem/principle-core';
import { ServiceCodeType } from '@principle-theorem/principle-core/interfaces';
import { getError, isSameRef, type WithRef } from '@principle-theorem/shared';
import { compact } from 'lodash';
import {
  BaseFeeScheduleDestination,
  IFeeScheduleJobData,
  IFeeScheduleMigrationData,
} from '../../../destination/entities/fee-schedules';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  FeeScheduleSourceEntity,
  OasisFeeSchedule,
} from '../../source/entities/fee-schedule';
import { OasisFeeScheduleMappingHandler } from '../mappings/fee-schedules';
import { OasisPracticeMappingHandler } from '../mappings/practices';

export class FeeScheduleDestinationEntity extends BaseFeeScheduleDestination<OasisFeeSchedule> {
  feeScheduleSourceEntity = new FeeScheduleSourceEntity();
  practiceCustomMapping = new OasisPracticeMappingHandler();
  feeScheduleCustomMapping = new OasisFeeScheduleMappingHandler();

  buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IFeeScheduleJobData<OasisFeeSchedule>
  ):
    | IFeeScheduleMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    try {
      if (
        data.feeScheduleRecord.record.status ===
        SourceEntityRecordStatus.Invalid
      ) {
        return this._buildErrorResponse(
          data.feeScheduleRecord.record,
          'Source fee schedule is invalid'
        );
      }

      const practice = data.practices.find((searchPractice) =>
        isSameRef(
          searchPractice.destinationIdentifier,
          migration.configuration.practices[0].ref
        )
      );

      if (!practice || !practice.destinationIdentifier) {
        return this._buildErrorResponse(
          data.feeScheduleRecord.record,
          `Can't find practice. Make sure the practice ids have been mapped.`
        );
      }

      return {
        isDefault: false,
        practiceRef: practice.destinationIdentifier,
        organisationRef: migration.configuration.organisation.ref,
        sourceFeeScheduleId: this.feeScheduleSourceEntity
          .getSourceRecordId(data.feeScheduleRecord.data)
          .toString(),
        feeSchedule: FeeSchedule.init({
          name: data.feeScheduleRecord.data.name,
          serviceCodeType: ServiceCodeType.ADA,
          serviceCodes: compact(
            data.feeScheduleRecord.data.items.map((item) => {
              const foundServiceCode = ServiceProviderHandler.findServiceCode(
                item.code
              );

              if (!foundServiceCode) {
                // eslint-disable-next-line no-console
                console.error(
                  `Can't find service code info for code ${item.itemId}`
                );
                return;
              }

              return serviceCodeToFee(foundServiceCode, item.price);
            })
          ),
        }),
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.feeScheduleRecord.record,
        getError(error)
      );
    }
  }
}
